// ---- Projectiles (client render) ----
// The cosmetic flying-arrow layer for ranged combat. Projectiles are SERVER-
// AUTHORITATIVE and attacker-agnostic: the server rolls the hit, then broadcasts a
// `{ t:"projectile", from, to, speed, hit, at }` to every client (net.ts → spawn()),
// which flies an arrow from `from` to `to`. This module never decides anything —
// it just plays back what the server sends, so the same code renders the player's
// bow now and an enemy archer's shot later.
//
// A HIT flies straight to the target point and vanishes on arrival (the damage +
// flinch land server-side at that same moment — see index.ts pending-hits). A MISS
// is spawned with its endpoint already nudged wide/long by the server, so the arrow
// visibly sails past; on arrival we spawn a muted "Miss" float at the intended
// target (`at*`) so nearby clients get the same feedback as a red damage number.
// Flight is pure lerp at the weapon's projectileSpeed; the mesh is oriented along
// its velocity. No physics, no collision — decorative only.

import * as THREE from "three";
import { toonMat } from "./toon.ts";
import { spawnMissFloat } from "./dmgfloat.ts";

/** One in-flight arrow. */
type Shot = {
  mesh: THREE.Group;
  from: THREE.Vector3;
  to: THREE.Vector3;
  dist: number;
  speed: number;
  travelled: number; // world units flown so far
  /** When false, spawn a Miss float at `at` on arrival. */
  hit: boolean;
  atX: number; atY: number; atZ: number;
};

/** Parameters of a fired shot, as they arrive from the server broadcast. */
export type ProjectileSpawn = {
  fromX: number; fromY: number; fromZ: number;
  toX: number; toY: number; toZ: number;
  speed: number;
  hit: boolean;
  /** Intended impact (target chest) — Miss float anchor; ignored on hits. */
  atX: number; atY: number; atZ: number;
};

let scene: THREE.Scene;
const shots: Shot[] = [];
const _dir = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);

export function initProjectiles(s: THREE.Scene): void {
  scene = s;
}

// Every arrow shares one geometry set (the toonMat pattern) — a shot allocates
// only the Group + Mesh wrappers, never fresh geometry, and nothing disposes.
const SHAFT_GEO = new THREE.CylinderGeometry(0.02, 0.02, 0.8, 6);
const HEAD_GEO = new THREE.ConeGeometry(0.05, 0.16, 6);
const FLETCH_GEO = new THREE.BoxGeometry(0.12, 0.006, 0.14);

/** Build a slim arrow group pointing along +z (its "forward"), so we can aim it by
 *  rotating the group to face its velocity. Shaft + steel head + red fletching. */
function buildArrow(): THREE.Group {
  const g = new THREE.Group();
  const shaft = new THREE.Mesh(SHAFT_GEO, toonMat(0x9c7a4d));
  shaft.rotation.x = Math.PI / 2; // lay the cylinder along +z
  g.add(shaft);
  const head = new THREE.Mesh(HEAD_GEO, toonMat(0x8b8f96));
  head.rotation.x = Math.PI / 2;
  head.position.z = 0.46;
  g.add(head);
  const fletch = new THREE.Mesh(FLETCH_GEO, toonMat(0xcc4433));
  fletch.position.z = -0.34;
  g.add(fletch);
  for (const m of g.children) (m as THREE.Mesh).castShadow = true;
  return g;
}

/** Launch an arrow. Called by net.ts on a `projectile` broadcast (any attacker). */
export function spawnProjectile(p: ProjectileSpawn): void {
  const from = new THREE.Vector3(p.fromX, p.fromY, p.fromZ);
  const to = new THREE.Vector3(p.toX, p.toY, p.toZ);
  const mesh = buildArrow();
  mesh.position.copy(from);
  aim(mesh, from, to);
  scene.add(mesh);
  shots.push({
    mesh,
    from,
    to,
    dist: from.distanceTo(to),
    speed: p.speed > 0 ? p.speed : 40,
    travelled: 0,
    hit: p.hit,
    atX: p.atX, atY: p.atY, atZ: p.atZ,
  });
}

/** Orient the arrow group so its +z points from `from` toward `to`. */
function aim(mesh: THREE.Group, from: THREE.Vector3, to: THREE.Vector3): void {
  _dir.subVectors(to, from);
  if (_dir.lengthSq() < 1e-6) return;
  _dir.normalize();
  mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), _dir);
  // No roll: the fletching orientation is cosmetic.
  void _up;
}

/** Despawn every in-flight arrow — a map swap invalidates their from/to (old-map
 *  world coords), and the meshes live on the shared scene, not the world group,
 *  so they'd keep flying through the new terrain otherwise. */
export function clearProjectiles(): void {
  // Geometry + materials are shared module-level caches — removal is enough.
  for (const s of shots.splice(0)) scene.remove(s.mesh);
}

/** Advance every in-flight arrow; despawn on arrival. Call each frame from game.ts. */
export function updateProjectiles(dt: number): void {
  for (let i = shots.length - 1; i >= 0; i--) {
    const s = shots[i]!;
    s.travelled += s.speed * dt;
    const k = s.dist > 0 ? s.travelled / s.dist : 1;
    if (k >= 1) {
      // Miss feedback lands with the arrow (hits use the HP-drop damage number).
      // Slight lift so it sits near head-height the way dmg floats do.
      if (!s.hit) spawnMissFloat(s.atX, s.atY + 0.45, s.atZ);
      scene.remove(s.mesh);
      shots.splice(i, 1);
      continue;
    }
    s.mesh.position.lerpVectors(s.from, s.to, k);
  }
}

